/**
 * <s-swatch-preview> — product card color preview.
 *
 * Wraps a card's swatch list. When a mouse hovers a swatch link (or a keyboard
 * user focuses one), the card's main product image swaps to that color's photo
 * from the link's data-preview-srcset / data-preview-src. Leaving the swatch
 * list restores the original photo. Touch taps are untouched: they follow the
 * link as before.
 *
 * The card image is the first <img> in the closest <article> that is not inside
 * this element.
 */
class SwatchPreview extends HTMLElement {
  #image = null;
  #original = null;
  #token = 0;

  connectedCallback() {
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  disconnectedCallback() {
    this.#restore();
  }

  #cardImage() {
    if (this.#image?.isConnected) return this.#image;
    const card = this.closest('article');
    if (!card) return null;
    this.#image = [...card.querySelectorAll('img')].find((img) => !this.contains(img)) ?? null;
    return this.#image;
  }

  #onPointerOver = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    const link = event.target.closest('a[data-preview-src]');
    if (link && this.contains(link)) this.#preview(link);
  };

  #onPointerLeave = () => this.#restore();

  #onFocusIn = (event) => {
    const link = event.target.closest('a[data-preview-src]');
    if (link) this.#preview(link);
  };

  #onFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.#restore();
  };

  async #preview(link) {
    const image = this.#cardImage();
    if (!image) return;
    const src = link.dataset.previewSrc;
    const srcset = link.dataset.previewSrcset || '';
    if (!this.#original) this.#original = { src: image.getAttribute('src'), srcset: image.getAttribute('srcset') };
    if (image.getAttribute('src') === src) return;

    const token = ++this.#token;
    // Decode off-screen first so the swap never flashes an empty frame.
    const loader = new Image();
    loader.sizes = image.sizes;
    if (srcset) loader.srcset = srcset;
    loader.src = src;
    try {
      await loader.decode();
    } catch {
      return;
    }
    if (token !== this.#token) return;
    if (srcset) image.setAttribute('srcset', srcset);
    else image.removeAttribute('srcset');
    image.setAttribute('src', src);
  }

  #restore() {
    this.#token++;
    const image = this.#image;
    if (!image || !this.#original) return;
    if (this.#original.srcset) image.setAttribute('srcset', this.#original.srcset);
    else image.removeAttribute('srcset');
    image.setAttribute('src', this.#original.src);
    this.#original = null;
  }
}

if (!customElements.get('s-swatch-preview')) {
  customElements.define('s-swatch-preview', SwatchPreview);
}
